/**
 * Hebrew numeral helpers for Torah-style chapter / verse numbering.
 *
 * Examples:
 *    1 →  א׳
 *    9 →  ט׳
 *   10 →  י׳
 *   11 →  י״א
 *   15 →  ט״ו   (NOT י״ה — would spell יה, a divine name)
 *   16 →  ט״ז   (NOT י״ו — would spell יו, also a divine reference)
 *   22 →  כ״ב
 *   30 →  ל׳
 *  150 →  ק״נ
 *  300 →  ש׳
 *
 * Rules implemented:
 *   • Numbers 1–9, 10, 20, 30, …, 100, 200, 300, 400  → single letter
 *     followed by a geresh (״׳״)
 *   • Compound numbers (e.g. 11, 22, 150) → "tens+ones" or
 *     "hundreds+tens+ones" with a gershayim (״״״) between the last two
 *     letters
 *   • 15 and 16 are written ט״ו / ט״ז (not י״ה / י״ו) — sacred-name
 *     avoidance, the universal Jewish typographic convention
 *
 * The function returns a plain string suitable for direct rendering
 * inside any Hebrew UI element.
 */

const ONES = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
const TENS = ["", "י", "כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ"];
const HUNDREDS = ["", "ק", "ר", "ש", "ת"]; // 100..400; 500+ falls back

const GERESH = "\u05F3"; // ׳
const GERSHAYIM = "\u05F4"; // ״

export function numberToHebrewLetter(num) {
  if (num == null || Number.isNaN(num)) return "";
  let n = Math.floor(Math.abs(Number(num)));
  if (n === 0) return "0"; // Tanakh has no chapter 0 — keep numeric fallback
  if (n > 999) {
    // Beyond our table — fall back to digits so we don't truncate state
    // info (no Tanakh chapter exceeds 150, so this is purely defensive).
    return String(num);
  }

  // Build the letters from largest to smallest.
  const letters = [];

  // Hundreds (100..900). For 500..900 we stack ת + smaller hundred
  // letters (e.g. 500 = ת״ק, 700 = ת״ש). Tanakh chapter numbers never
  // reach this range, but we handle it cleanly for verses anyway.
  const h = Math.floor(n / 100);
  if (h > 0) {
    if (h <= 4) {
      letters.push(HUNDREDS[h]);
    } else {
      // 500 = ת + ק (400 + 100), 800 = ת + ת (400 + 400), 900 = ת + ת + ק
      let rem = h;
      while (rem > 0) {
        const take = rem >= 4 ? 4 : rem;
        letters.push(HUNDREDS[take]);
        rem -= take;
      }
    }
    n %= 100;
  }

  // 15 and 16 — sacred-name avoidance: use ט+ו / ט+ז instead of י+ה / י+ו
  if (n === 15) {
    letters.push("ט", "ו");
    n = 0;
  } else if (n === 16) {
    letters.push("ט", "ז");
    n = 0;
  } else {
    const t = Math.floor(n / 10);
    if (t > 0) {
      letters.push(TENS[t]);
      n %= 10;
    }
    if (n > 0) {
      letters.push(ONES[n]);
    }
  }

  // Punctuation: geresh on single-letter values, gershayim before the
  // last letter for multi-letter values.
  if (letters.length === 1) {
    return letters[0] + GERESH;
  }
  return letters.slice(0, -1).join("") + GERSHAYIM + letters[letters.length - 1];
}

/**
 * Returns true when the user-visible language is Hebrew. Centralised so
 * components don't each duplicate the localStorage / i18next check.
 */
export function isHebrewUi() {
  try {
    const saved = window.localStorage.getItem("torahya_lang");
    return saved !== "en";
  } catch {
    return true;
  }
}

/**
 * Display helper — returns Hebrew letter for Hebrew UI, decimal for English.
 */
export function chapterDisplay(num) {
  if (isHebrewUi()) return numberToHebrewLetter(num);
  return String(num);
}
